<template>
  <div class="input-screen-page-wrapper" style="width: 100%; height: 100%">
    <!-- Menu options -->

    <inputs-grid
      ref="inputsGrid"
      :additional-columns="additionalColumns"
      :save-changes="saveChanges"
      :column-validations="columnValidations"
      :additional-dependent-columns="dependentColumns"
      :is-saveable="isSaveable"
      :is-not-saveable-reason="$t('tooltip.saveDisabledInvalidInputsMessage')"
      :export-action="downloadOverrides"
      :is-exporting="downloading"
      route-suffix="overrides"
      :columns-to-force-refresh-on-save="['overridePrice::price']"
    >
      <!-- Slots for inputs-grid, -->
      <!-- Import/ Add buttons etc. -->
      <template #buttons>
        <price-overrides-upload v-if="canEditPriceOverrides" />
      </template>
    </inputs-grid>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import axios from 'axios';
import to from 'await-to-js';
import moment from 'moment';
import { get, startCase, isEmpty } from 'lodash';
import tablesMixin from '../../../mixins/tables';
import inputScreensTableMixin from '../../../mixins/inputScreensTable';
import featureFlagsMixin from '../../../mixins/featureFlags';
import priceOverrideMixin from '../../../mixins/priceOverride';
import { yearMonthDayFormat } from '@enums/date-formats';
import { maxDateTimestamp } from '@enums/dates';
import numberFormats from '@enums/number-formats';
import { useZones, displayHierarchyAndFilter, displayClientProductKey } from '@enums/feature-flags';
import { inputTableCssClasses } from '@enums/tables';
import DataTypes from '@enums/data-types';
import { categoryLevel, pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import overrideTypes from '@enums/override-types';
import datePickerEditor from '../../../components/date-picker-editor.vue';
import inputsPriceOverrideCellRenderer from '../../../components/ag-grid-cell-renderers/inputs-price-override-cell-renderer.vue';
import {
  validations,
  localisedNumericCol,
  localisedDateCol,
  localisedDateValueParser,
} from '../../../utils/inputs-grid-utils';
import agGridUtils from '../../../utils/ag-grid-utils';

const requiresPrice = {
  message: 'needPrice',
  test(params) {
    const hasEffectiveDate = Boolean(get(params, 'node.data.overridePrice.effectiveDate'));
    const hasOverridePrice = Boolean(get(params, 'node.data.overridePrice.price'));
    return !(hasEffectiveDate && !hasOverridePrice);
  },
};
const requiresExpireDate = {
  message: 'needExpireDate',
  test(params) {
    const hasEffectiveDate = Boolean(get(params, 'node.data.overridePrice.effectiveDate'));
    const hasExpiryDate = Boolean(get(params, 'node.data.overridePrice.expiryDate'));
    return !(hasEffectiveDate && !hasExpiryDate);
  },
};

const effectiveDateBeforeExpiryDate = {
  message: 'effectiveDateBeforeExpiryDate',
  test(params) {
    const effectiveDate = get(params, 'node.data.overridePrice.effectiveDate');
    const expiryDate = get(params, 'node.data.overridePrice.expiryDate');
    // return false if data is valid. otherwise display message.
    // data is valid when effectiveDate is < expiryDate, or neither are set.
    return !(effectiveDate && expiryDate && effectiveDate > expiryDate);
  },
};

const { greaterThan0 } = validations;

export default {
  name: 'PriceOverridesEditor',
  components: {
    // register vue components
    // eslint thinks that it's not used, but headerComponent points to it
    // eslint-disable-next-line vue/no-unused-components
    datePickerEditor,
    // eslint-disable-next-line vue/no-unused-components
    inputsPriceOverrideCellRenderer,
  },

  mixins: [featureFlagsMixin, tablesMixin, inputScreensTableMixin, priceOverrideMixin],

  async beforeRouteLeave(destination, from, next) {
    const preventNavigation = await this.$refs.inputsGrid.resolveEditsBeforeNavigation();

    if (preventNavigation) return;

    next();
  },

  data() {
    return {
      get,
      startCase,
      invalidRows: {},
      showDatePicker: false,
      selectedCellData: null,
      maxMomentDate: moment.utc(maxDateTimestamp),
      columnValidations: {
        'overridePrice::price': { greaterThan0, requiresPrice },
        'overridePrice::expiryDate': { requiresExpireDate, effectiveDateBeforeExpiryDate },
      },
      dependentColumns: {
        'overridePrice::effectiveDate': ['overridePrice::price', 'overridePrice::expiryDate'],
      },
      priceOverrideFields: [
        { field: 'price', type: DataTypes.number, text: this.$t('priceOverrides.editor.price') },
        {
          field: 'referencePriceAtOverride',
          type: DataTypes.number,
          text: this.$t('priceOverrides.editor.referencePriceAtOverride'),
        },
        {
          field: 'effectiveDate',
          type: DataTypes.str,
          text: this.$t('priceOverrides.editor.effectiveDate'),
        },
        {
          field: 'expiryDate',
          type: DataTypes.str,
          text: this.$t('priceOverrides.editor.expiryDate'),
        },
      ],
    };
  },

  computed: {
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapState('scenarioProducts', ['downloading']),
    ...mapGetters('context', ['isPricingSpecialist']),
    ...mapState('clientConfig', ['i18nconfig', 'exportConfigs']),
    ...mapState('filters', ['retailAttributesFilter']),
    ...mapGetters('scenarioMetadata', ['lockedPricesByScenarioKeyTsgKey']),

    zonesEnabled() {
      return this.isFeatureFlagEnabled(useZones);
    },

    canEditPriceOverrides() {
      return this.isPricingSpecialist;
    },

    getTodaysDate() {
      return moment().format(yearMonthDayFormat);
    },

    isSaveable() {
      return isEmpty(this.invalidRows);
    },

    toolStoreGroupColumn() {
      return this.isFeatureFlagEnabled(useZones)
        ? {
            text: this.$t('priceOverrides.editor.toolStoreGroup'),
            value: 'toolStoreGroupDescription',
            sortable: true,
            align: 'start',
            class: `m-width ${inputTableCssClasses.threeFixedColumns}`,
            dataType: DataTypes.str,
          }
        : null;
    },

    showHierarchy() {
      return this.isFeatureFlagEnabled(displayHierarchyAndFilter);
    },

    hierarchyHeaders() {
      return this.showHierarchy
        ? [
            {
              text: this.$t('pricing.category'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${categoryLevel}.levelEntryDescription`,
              class: 'm-width',
              dataType: DataTypes.str,
            },
            {
              text: this.$t('pricing.pricingGroup'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${pricingGroupLevel}.levelEntryDescription`,
              class: 'm-width',
              dataType: DataTypes.str,
            },
            {
              text: this.$t('pricing.architectureGroup'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${architectureGroupLevel}.levelEntryDescription`,
              class: 'border-right m-width',
              dataType: DataTypes.str,
            },
          ]
        : [];
    },

    headers() {
      const tsgHeader = this.toolStoreGroupColumn ? [this.toolStoreGroupColumn] : [];
      return [
        {
          text: this.$t('priceOverrides.editor.productKey'),
          align: 'start',
          sortable: true,
          value: 'productKeyDisplay',
          class: `s-width ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
          formatter: {
            type: get(
              this.exportConfigs,
              'exportToExcel.columnFormatter.productKeyDisplay',
              numberFormats.integer
            ),
          },
        },
        ...tsgHeader,
        {
          text: this.$t('priceOverrides.editor.productDescription'),
          align: 'start',
          sortable: true,
          value: 'productDescription',
          class: `l-width border-right ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
        },
        ...this.hierarchyHeaders,
        {
          text: this.$t('priceOverrides.editor.price'),
          align: 'start',
          sortable: true,
          value: 'overridePrice.price',
          dataType: DataTypes.number,
        },
        {
          text: this.$t('priceOverrides.editor.referencePriceAtOverride'),
          align: 'start',
          sortable: true,
          value: 'overridePrice.referencePriceAtOverride',
          dataType: DataTypes.number,
        },
        {
          text: this.$t('priceOverrides.editor.effectiveDate'),
          align: 'start',
          sortable: true,
          value: 'overridePrice.effectiveTimestamp',
          isDate: true,
          dataType: DataTypes.number,
          formatter: {
            type: DataTypes.date,
            format: this.dateFormats.clientDateFormatForMoment,
          },
          comparator: agGridUtils.utils.createTimestampComparator(
            'overridePrice.effectiveTimestamp'
          ),
        },
        {
          text: this.$t('priceOverrides.editor.expiryDate'),
          align: 'start',
          sortable: true,
          value: 'overridePrice.expiryTimestamp',
          isDate: true,
          dataType: DataTypes.number,
          formatter: {
            type: DataTypes.date,
            format: this.dateFormats.clientDateFormatForMoment,
          },
          comparator: agGridUtils.utils.createTimestampComparator('overridePrice.expiryTimestamp'),
        },
      ];
    },

    exportTranslationMap() {
      const hierarchyColumnTranslations = this.showHierarchy
        ? {
            [`hierarchy.${categoryLevel}.levelEntryDescription`]: this.$t('pricing.category'),
            [`hierarchy.${pricingGroupLevel}.levelEntryDescription`]: this.$t(
              'pricing.pricingGroup'
            ),
            [`hierarchy.${architectureGroupLevel}.levelEntryDescription`]: this.$t(
              'pricing.architectureGroup'
            ),
          }
        : {};
      const priceOverrideFields = this.priceOverrideFields.reduce((acc, fieldObj) => {
        acc[fieldObj.field] = fieldObj.text;
        return acc;
      }, {});

      const toolStoreGroupColumn = this.zonesEnabled
        ? { toolStoreGroupDescription: this.$t('pricing.toolStoreGroup') }
        : {};

      const clientProductKeyColumn = this.isFeatureFlagEnabled(displayClientProductKey)
        ? { clientProductKey: this.$t('pricing.clientProductKey') }
        : {};

      return {
        productKeyDisplay: this.$t('priceOverrides.editor.productKey'),
        ...clientProductKeyColumn,
        ...toolStoreGroupColumn,
        productDescription: this.$t('priceOverrides.editor.productDescription'),
        ...hierarchyColumnTranslations,
        ...priceOverrideFields,
      };
    },

    additionalColumns() {
      return [
        {
          headerName: this.$t('linkText.priceOverrides'),
          groupId: `priceOverrides`,
          children: [
            {
              headerName: this.$t('priceOverrides.editor.price'),
              colId: `overridePrice::price`,
              field: `overridePrice.price`,
              onCellValueChanged: this.onCellValueChangedHandler,
              ...localisedNumericCol,
              editable: params =>
                get(params, `data.hierarchy[${architectureGroupLevel}].id`) &&
                !get(this.lockedPricesByScenarioKeyTsgKey, get(params, `data.scenarioKey`), {})[
                  get(params, `data.toolStoreGroupKey`)
                ],
              cellClassRules: {
                'to-be-expired-background': this.isToBeExpired,
              },
              cellRenderer: 'inputsPriceOverrideCellRenderer',
              tooltipValueGetter: params => {
                return this.isActiveTransitionPeriodOverride(params.data)
                  ? { additionalText: [this.$t('gridView.tooltips.transitionPeriodOverride')] }
                  : null;
              },
            },
            {
              headerName: this.$t('priceOverrides.editor.referencePriceAtOverride'),
              colId: `overridePrice::referencePriceAtOverride`,
              field: `overridePrice.referencePriceAtOverride`,
              tooltipValueGetter: this.tooltipValueGetter,
              ...localisedNumericCol,
              editable: false,
              cellClassRules: {
                'to-be-expired-background': this.isToBeExpired,
              },
            },
            {
              headerName: this.$t('priceOverrides.editor.effectiveDate'),
              colId: `overridePrice::effectiveDate`,
              field: `overridePrice.effectiveDate`,
              valueFormatter: params => this.formatDateValue(params.value),
              cellEditorParams: {
                firstDayOfWeek: this.i18nconfig.firstDayOfTheWeek,
                locale: this.i18nconfig.fallbackLocale,
                minDate: () => this.getTodaysDate,
                // TODO: PRICE-5765 add max param to date-picker-editor and use here to prevent effectiveDateBeforeExpiryDate
                // maxDate: params => {
                //   return this.getDateValue(params.data.overridePrice.expiryDate, false);
                // },
                dateValue: date => this.getDateValue(date, true),
              },
              cellClassRules: {
                'to-be-expired-background': this.isToBeExpired,
              },
              ...localisedDateCol,
              valueParser: params =>
                localisedDateValueParser({
                  params,
                  clientDateFormatForMoment: this.dateFormats.clientDateFormatForMoment,
                }),
              editable: this.effectiveExpiryDatesEditable,
              comparator: agGridUtils.utils.createTimestampComparator(
                'overridePrice.effectiveTimestamp'
              ),
            },
            {
              headerName: this.$t('priceOverrides.editor.expiryDate'),
              colId: `overridePrice::expiryDate`,
              field: `overridePrice.expiryDate`,
              valueFormatter: params => this.formatDateValue(params.value),
              cellEditorParams: {
                firstDayOfWeek: this.i18nconfig.firstDayOfTheWeek,
                locale: this.i18nconfig.fallbackLocale,
                minDate: params => {
                  return (
                    this.getDateValue(params.data.overridePrice.effectiveDate, true) ||
                    this.getTodaysDate
                  );
                },
                dateValue: date => this.getDateValue(date, true),
              },
              cellClassRules: {
                'to-be-expired-background': this.isToBeExpired,
              },
              ...localisedDateCol,
              valueParser: params =>
                localisedDateValueParser({
                  params,
                  clientDateFormatForMoment: this.dateFormats.clientDateFormatForMoment,
                }),
              editable: this.effectiveExpiryDatesEditable,
              onCellValueChanged: this.onExpiryDateValueChangedHandler,
              comparator: agGridUtils.utils.createTimestampComparator(
                'overridePrice.expiryTimestamp'
              ),
            },
          ],
        },
      ];
    },
  },

  methods: {
    ...mapActions('scenarioProducts', ['downloadPriceOverrides']),
    ...mapMutations('notifications', ['addNotification']),
    ...mapMutations('gridView', ['setTemporaryUnsavedKpiResults']),
    tooltipValueGetter(params) {
      return this.$refs.inputsGrid.tooltipValueGetter(params);
    },
    isMainbanner(toolStoreGroupKey) {
      // If not using zones, you only have mainbanner
      if (!this.zonesEnabled) return true;

      return toolStoreGroupKey === this.mainTsgKey;
    },

    downloadOverrides() {
      this.downloadPriceOverrides({
        where: this.retailAttributesFilter,
        translationMap: this.exportTranslationMap,
        columnFormatters: this.getColumnFormatters(this.headers),
      });
    },

    async saveChanges({ updates, mainTsgKey }) {
      const [error, results] = await to(
        axios.post(`/api/inputs/workpackage/${this.selectedWorkpackage._id}/overrides`, {
          updates,
          mainTsgKey,
        })
      );
      if (error) {
        let notificationPayload = '';
        if (get(error, 'response.data.message', '').includes('E11000 duplicate key error')) {
          notificationPayload = this.$t('notifications.price_override.error.duplicateKey');
        }
        this.addNotification({
          id: `${new Date()}priceOverride`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.price_override',
          notificationPayload,
        });
      } else {
        const [, dummyKpiResult] = (results && results.data) || [];
        const hasChanges =
          dummyKpiResult && (dummyKpiResult.modifiedCount > 0 || dummyKpiResult.upsertedCount > 0);
        if (hasChanges) {
          // highlight the recalculate all buttons
          this.setTemporaryUnsavedKpiResults(true);
        }
      }
      return results;
    },

    onCellValueChangedHandler(params) {
      if (params.column.colId === 'overridePrice::price') {
        const newValue = params.data.overridePrice.price
          ? this.getReferencePriceAtOverride(params.data)
          : null;
        const rowNode = params.node;
        const colKey = 'overridePrice::referencePriceAtOverride';
        rowNode.setDataValue(colKey, newValue);
      }
    },

    onExpiryDateValueChangedHandler(params) {
      if (params.column.colId === 'overridePrice::expiryDate') {
        const effectiveDate = get(params, 'data.overridePrice.effectiveDate');
        if (!effectiveDate) {
          const rowNode = params.node;
          const colKey = 'overridePrice::effectiveDate';
          rowNode.setDataValue(colKey, this.getTodaysDate);
        }
      }
    },

    getDateValue(date, checkOnMaxDate) {
      if (!checkOnMaxDate || !date) {
        return date;
      }
      return this.maxDateExceeded(date) ? null : date;
    },

    isToBeExpired(params) {
      const toBeExpired = get(params.data, 'overridePrice.toBeExpired', null);
      return toBeExpired;
    },

    /**
     * Returns true if the effective and expiry date should be editable based on whether prices
     * are locked and whether the existing override is a transition period override.
     * @param params agGrid's EditableCallbackParams instance containing row data.
     */
    effectiveExpiryDatesEditable(params) {
      return (
        !get(this.lockedPricesByScenarioKeyTsgKey, get(params, `data.scenarioKey`), {})[
          get(params, `data.toolStoreGroupKey`)
        ] && get(params, `data.overridePrice.overrideType`) !== overrideTypes.transitionPeriod
      );
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';

.main-header th {
  position: relative;
}

#buttons-bar {
  max-width: 20rem;
}

.v-list-item__title {
  font-size: 1.5rem;
}

.v-list-item {
  min-height: 35px;
}

.no-data-slot {
  padding-left: 2.2rem;
}

.input-screen-page-wrapper .table-cell::v-deep .tooltipped-truncated-field {
  position: unset;
}

.date-attribute-cell {
  position: relative;
}

.pale {
  opacity: 0.7;
}

.to-be-expired-background {
  background-color: $ag-grid-to-be-expired-background-color;
}
</style>
